import translationKeys from "./translationKeys.json";
import styles from "./TournamentPageTabs.module.css";
import { useState } from "react";
import Link from "next/link";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import {
  getTotalPrize,
  isElevatedRound,
  isRoundNumber456,
  prizeCalculator,
  prizeCalculatorPGA,
  prizeCalculatorSeason3,
} from "../../utils/tournaments";
import {
  AGGREGATE_CTP,
  AGGREGATE_CTP_FINALE,
  AGGREGATE_CTP_LAST,
  AGGREGATE_CTP_4_5_6_ROUNDS,
  BIRDIE_STREAK,
  BIRDIE_STREAK_LAST,
  BIRDIE_STREAK_4_5_6_ROUNDS,
  TOURNAMENT_IDS_SEASON_1,
  INITIAL_PURSE,
  LONGEST_DRIVE,
  LONGEST_DRIVE_LAST,
  LONGEST_DRIVE_4_5_6_ROUNDS,
  MOST_GIRS,
  MOST_GIRS_LAST,
  SOCIAL_MEDIA,
  SOCIAL_MEDIA_FINALE,
  SOCIAL_MEDIA_LAST,
  CTP_PGA,
  INITIAL_PURSE_PGA,
  ENTRY_FEE_PGA,
  AGGREGATE_CTP_SEASON_3,
  LONGEST_DRIVE_SEASON_3,
  SOCIAL_MEDIA_SEASON_3,
  CTP_SEASON_3,
  LONGEST_DRIVE_SEASON_3_FINAL,
  PRIZES_FINAL_SEASON_2,
  INITIAL_PURSE_SEASON_3_FINAL,
  PRIZES_FINAL_SEASON_3,
  ENTRY_FEE,
} from "../../utils/constants";
import { PRIZE_TABLE } from "../PrizeList/PrizeListSeason1";

interface PurseProps {
  playersCount: number;
  tournamentName: string;
  isFinalSeason1: boolean;
  tournamentId: string;
  isPGATournament: boolean;
  tournamentSeason: number;
}

const Purse = ({
  playersCount,
  tournamentName,
  isFinalSeason1,
  tournamentId,
  isPGATournament,
  tournamentSeason,
}: PurseProps) => {
  const [inputVal, setInputVal] = useState("");

  const isElevated = isElevatedRound(tournamentSeason, tournamentName);
  const isFinalSeason2 =
    tournamentSeason === 2 && tournamentName?.toLowerCase()?.includes("final");
  const isFinalSeason3 =
    tournamentSeason === 3 && tournamentName?.toLowerCase()?.includes("final");
  const isRound456 = isRoundNumber456(tournamentName);
  const tournamentRoundSeason1 = `round_${
    TOURNAMENT_IDS_SEASON_1.indexOf(tournamentId) + 1
  }`;

  const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    setInputVal(target.value);
  };

  const getPrizeList = () => {
    if (tournamentSeason == 1) {
      return PRIZE_TABLE[tournamentRoundSeason1];
    }

    if (tournamentSeason == 2) {
      return isFinalSeason2
        ? PRIZES_FINAL_SEASON_2
        : prizeCalculator(
            playersCount,
            tournamentSeason,
            isElevated,
            isRound456
          );
    }

    if (tournamentSeason == 3) {
      return isPGATournament
        ? prizeCalculatorPGA(playersCount)
        : isFinalSeason3
        ? PRIZES_FINAL_SEASON_3
        : prizeCalculatorSeason3(playersCount);
    }
  };

  const prizeList = getPrizeList();

  const aggregateCtpPrizes = () => {
    if (tournamentSeason === 1) {
      return AGGREGATE_CTP_LAST;
    }

    if (isRound456) {
      return AGGREGATE_CTP_4_5_6_ROUNDS;
    }

    if (isFinalSeason2) {
      return AGGREGATE_CTP_FINALE;
    }

    return AGGREGATE_CTP;
  };

  const birdieStreakPrizes = () => {
    if (tournamentSeason === 1) {
      return BIRDIE_STREAK_LAST;
    }

    if (isRound456) {
      return BIRDIE_STREAK_4_5_6_ROUNDS;
    }

    return BIRDIE_STREAK;
  };

  const longestDrivePrizes = () => {
    if (tournamentSeason === 1) {
      return LONGEST_DRIVE_LAST;
    }

    if (isRound456) {
      return LONGEST_DRIVE_4_5_6_ROUNDS;
    }

    return LONGEST_DRIVE;
  };

  if (isPGATournament) {
    return (
      <div className={styles.pursePageContainer}>
        <div className={styles.pursePageWrapper}>
          <ul className={styles.purseUlList}>
            <li>
              <span>{translationKeys.purse}</span>
              <span>
                $
                {(
                  INITIAL_PURSE_PGA +
                  ENTRY_FEE_PGA * playersCount
                ).toLocaleString("en-US")}
              </span>
            </li>
            <li>
              <span>{translationKeys.players}</span>
              <span>{playersCount}</span>
            </li>
          </ul>
          <div className={styles.purseInfoBlocks}>
            <div className={styles.purseInfoBlock}>
              <span className={styles.purseCalculateTitle}>
                {translationKeys.calculate}
              </span>
              <span className={styles.purseCalculateText}>
                {translationKeys.rank}
              </span>
              <input
                className="text-black"
                type="number"
                defaultValue={inputVal}
                onChange={handleInputChange}
              />
              <div className={styles.purseCalculateTitle}>
                $
                {prizeList[parseInt(inputVal) - 1]?.toLocaleString("en-US") ||
                  "0"}
              </div>
            </div>
            <div className={styles.purseInfoBlock}>
              <span>{translationKeys["stroke-play"]}</span>
              <ul className={styles.purseUlBlockList}>
                <li>
                  <span>1st</span>
                  <span>{prizeList[0]?.toLocaleString("en-US") || 0}</span>
                </li>
                <li>
                  <span>2nd</span>
                  <span>{prizeList[1]?.toLocaleString("en-US") || 0}</span>
                </li>
                <li>
                  <span>3rd</span>
                  <span>{prizeList[2]?.toLocaleString("en-US") || 0}</span>
                </li>
                <li>
                  <span>4th</span>
                  <span>{prizeList[3]?.toLocaleString("en-US") || 0}</span>
                </li>
                <li>
                  <span>5th</span>
                  <span>{prizeList[4]?.toLocaleString("en-US") || 0}</span>
                </li>
              </ul>
            </div>
            <div className={styles.purseInfoBlock}>
              <span>{translationKeys["ctp-all"]}</span>
              <ul className={styles.purseUlBlockList}>
                <li>
                  <span>1st</span>
                  <span>{CTP_PGA[0]}</span>
                </li>
                <li>
                  <span>2nd</span>
                  <span>{CTP_PGA[1]}</span>
                </li>
                <li>
                  <span>3rd</span>
                  <span>{CTP_PGA[2]}</span>
                </li>
                <li>
                  <span>4th</span>
                  <span>{CTP_PGA[3]}</span>
                </li>
                <li>
                  <span>5th</span>
                  <span>{CTP_PGA[4]}</span>
                </li>
              </ul>
            </div>
            <div
              className={`${styles.purseInfoBlock} ${styles.purseInfoBlockCentered}`}
            >
              <Link
                href={`/tournament/${tournamentName
                  ?.replace(/:/g, "")
                  .replace(/ - /g, " ")
                  .split(" ")
                  .join("-")
                  .toLowerCase()}/prize-list`}
              >
                <a className={styles.purseLink}>
                  <div>{translationKeys["prize-list"]}</div>
                  <div>
                    <EastOutlinedIcon fontSize="large" />
                  </div>
                </a>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isPGATournament && tournamentSeason === 3) {
    return (
      <div className={styles.pursePageContainer}>
        <div className={styles.pursePageWrapper}>
          <ul className={styles.purseUlList}>
            <li>
              <span>{translationKeys.purse}</span>
              <span>
                $
                {(isFinalSeason3
                  ? INITIAL_PURSE_SEASON_3_FINAL
                  : INITIAL_PURSE + ENTRY_FEE * playersCount
                ).toLocaleString("en-US")}
              </span>
            </li>
            <li>
              <span>{translationKeys.players}</span>
              <span>{playersCount}</span>
            </li>
          </ul>
          <div className={styles.purseInfoBlocks}>
            <div className={styles.purseInfoBlock}>
              <span className={styles.purseCalculateTitle}>
                {translationKeys.calculate}
              </span>
              <span className={styles.purseCalculateText}>
                {translationKeys.rank}
              </span>
              <input
                className="text-black"
                type="number"
                defaultValue={inputVal}
                onChange={handleInputChange}
              />
              <div className={styles.purseCalculateTitle}>
                $
                {prizeList[parseInt(inputVal) - 1]?.toLocaleString("en-US") ||
                  " 0"}
              </div>
            </div>
            <div className={styles.purseInfoBlock}>
              <span>{translationKeys["stroke-play"]}</span>
              <ul className={styles.purseUlBlockList}>
                <li>
                  <span>1st</span>
                  <span>{prizeList[0]?.toLocaleString("en-US") || 0}</span>
                </li>
                <li>
                  <span>2nd</span>
                  <span>{prizeList[1]?.toLocaleString("en-US") || 0}</span>
                </li>
                <li>
                  <span>3rd</span>
                  <span>{prizeList[2]?.toLocaleString("en-US") || 0}</span>
                </li>
                <li>
                  <span>4th</span>
                  <span>{prizeList[3]?.toLocaleString("en-US") || 0}</span>
                </li>
                <li>
                  <span>5th</span>
                  <span>{prizeList[4]?.toLocaleString("en-US") || 0}</span>
                </li>
              </ul>
            </div>
            <div className={styles.purseInfoBlock}>
              <span>{translationKeys["ctp-all"]}</span>
              <ul className={styles.purseUlBlockList}>
                <li>
                  <span>1st</span>
                  <span>{CTP_SEASON_3[0]}</span>
                </li>
                <li>
                  <span>2nd</span>
                  <span>{CTP_SEASON_3[1]}</span>
                </li>
                <li>
                  <span>3rd</span>
                  <span>{CTP_SEASON_3[2]}</span>
                </li>
              </ul>
            </div>
            <div className={styles.purseInfoBlock}>
              <span>{translationKeys["aggregate-ctp"]}</span>
              <ul className={styles.purseUlBlockList}>
                <li>
                  <span>1st</span>
                  <span>{AGGREGATE_CTP_SEASON_3[0]}</span>
                </li>
                <li>
                  <span>2nd</span>
                  <span>{AGGREGATE_CTP_SEASON_3[1]}</span>
                </li>
                <li>
                  <span>3rd</span>
                  <span>{AGGREGATE_CTP_SEASON_3[2]}</span>
                </li>
              </ul>
            </div>

            <div className={styles.purseInfoBlock}>
              <span>{translationKeys["longest-drive-men"]}</span>
              {isFinalSeason3 ? (
                <ul className={styles.purseUlBlockList}>
                  <li>
                    <span>1st</span>
                    <span>{LONGEST_DRIVE_SEASON_3_FINAL[0]}</span>
                  </li>
                  <li>
                    <span>2nd</span>
                    <span>{LONGEST_DRIVE_SEASON_3_FINAL[1]}</span>
                  </li>
                </ul>
              ) : (
                <ul className={styles.purseUlBlockList}>
                  <li>
                    <span>1st</span>
                    <span>{LONGEST_DRIVE_SEASON_3[0]}</span>
                  </li>
                  <li>
                    <span>2nd</span>
                    <span>{LONGEST_DRIVE_SEASON_3[1]}</span>
                  </li>
                  <li>
                    <span>3rd</span>
                    <span>{LONGEST_DRIVE_SEASON_3[2]}</span>
                  </li>
                </ul>
              )}
            </div>

            <div className={styles.purseInfoBlock}>
              <span>{translationKeys["longest-drive-women"]}</span>
              {isFinalSeason3 ? (
                <ul className={styles.purseUlBlockList}>
                  <li>
                    <span>1st</span>
                    <span>{LONGEST_DRIVE_SEASON_3_FINAL[0]}</span>
                  </li>
                  <li>
                    <span>2nd</span>
                    <span>{LONGEST_DRIVE_SEASON_3_FINAL[1]}</span>
                  </li>
                </ul>
              ) : (
                <ul className={styles.purseUlBlockList}>
                  <li>
                    <span>1st</span>
                    <span>{LONGEST_DRIVE_SEASON_3[0]}</span>
                  </li>
                  <li>
                    <span>2nd</span>
                    <span>{LONGEST_DRIVE_SEASON_3[1]}</span>
                  </li>
                  <li>
                    <span>3rd</span>
                    <span>{LONGEST_DRIVE_SEASON_3[2]}</span>
                  </li>
                </ul>
              )}
            </div>

            {!isFinalSeason3 && (
              <div className={styles.purseInfoBlock}>
                <span>{translationKeys["social-media"]}</span>
                <ul className={styles.purseUlBlockList}>
                  <li>
                    <span>1st</span>
                    <span>{SOCIAL_MEDIA_SEASON_3[0]}</span>
                  </li>
                  <li>
                    <span>2nd</span>
                    <span>{SOCIAL_MEDIA_SEASON_3[1]}</span>
                  </li>
                  <li>
                    <span>3rd</span>
                    <span>{SOCIAL_MEDIA_SEASON_3[2]}</span>
                  </li>
                </ul>
              </div>
            )}
            <div
              className={`${styles.purseInfoBlock} ${styles.purseInfoBlockCentered}`}
            >
              <Link
                href={`/tournament/${tournamentName
                  ?.replace(/:/g, "")
                  .replace(/ - /g, " ")
                  .split(" ")
                  .join("-")
                  .toLowerCase()}/prize-list`}
              >
                <a className={styles.purseLink}>
                  <div>{translationKeys["prize-list"]}</div>
                  <div>
                    <EastOutlinedIcon fontSize="large" />
                  </div>
                </a>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isFinalSeason1) {
    return (
      <div className={styles.pursePageContainer}>
        <div className={styles.pursePageWrapper}>
          <ul className={styles.purseUlList}>
            <li>
              <span>{translationKeys.purse}</span>
              <span>${INITIAL_PURSE.toLocaleString("en-US")}</span>
            </li>
            <li>
              <span>{translationKeys.players}</span>
              <span>{playersCount}</span>
            </li>
          </ul>
          <div className={styles.purseInfoBlocks}>
            <div className={styles.purseInfoBlock}>
              <span className={styles.purseCalculateTitle}>
                {translationKeys.calculate}
              </span>
              <span className={styles.purseCalculateText}>
                {translationKeys.rank}
              </span>
              <input
                className="text-black"
                type="number"
                defaultValue={inputVal}
                onChange={handleInputChange}
              />
              <div className={styles.purseCalculateTitle}>
                $
                {prizeList[parseInt(inputVal) - 1]?.toLocaleString("en-US") ||
                  " 0"}
              </div>
            </div>
            <div className={styles.purseInfoBlock}>
              <span>{translationKeys["stroke-play"]}</span>
              <ul className={styles.purseUlBlockList}>
                <li>
                  <span>1st</span>
                  <span>{prizeList[0]?.toLocaleString("en-US") || 0}</span>
                </li>
                <li>
                  <span>2nd</span>
                  <span>{prizeList[1]?.toLocaleString("en-US") || 0}</span>
                </li>
                <li>
                  <span>3rd</span>
                  <span>{prizeList[2]?.toLocaleString("en-US") || 0}</span>
                </li>
                <li>
                  <span>4th</span>
                  <span>{prizeList[3]?.toLocaleString("en-US") || 0}</span>
                </li>
                <li>
                  <span>5th</span>
                  <span>{prizeList[4]?.toLocaleString("en-US") || 0}</span>
                </li>
              </ul>
            </div>
            <div className={styles.purseInfoBlock}>
              <span>{translationKeys["aggregate-ctp"]}</span>
              <ul className={styles.purseUlBlockList}>
                <li>
                  <span>1st</span>
                  <span>{AGGREGATE_CTP_FINALE[0]}</span>
                </li>
                <li>
                  <span>2nd</span>
                  <span>{AGGREGATE_CTP_FINALE[1]}</span>
                </li>
                <li>
                  <span>3rd</span>
                  <span>{AGGREGATE_CTP_FINALE[2]}</span>
                </li>
              </ul>
            </div>
            <div className={styles.purseInfoBlock}>
              <span>{translationKeys["social-media"]}</span>
              <ul className={styles.purseUlBlockList}>
                <li>
                  <span>1st</span>
                  <span>{SOCIAL_MEDIA_FINALE[0]}</span>
                </li>
                <li>
                  <span>2nd</span>
                  <span>{SOCIAL_MEDIA_FINALE[1]}</span>
                </li>
                <li>
                  <span>3rd</span>
                  <span>{SOCIAL_MEDIA_FINALE[2]}</span>
                </li>
                <li>
                  <span>4th</span>
                  <span>{SOCIAL_MEDIA_FINALE[3]}</span>
                </li>
                <li>
                  <span>5th</span>
                  <span>{SOCIAL_MEDIA_FINALE[4]}</span>
                </li>
              </ul>
            </div>
            <div
              className={`${styles.purseInfoBlock} ${styles.purseInfoBlockCentered}`}
            >
              <Link
                href={`/tournament/${tournamentName
                  ?.replace(/:/g, "")
                  .replace(/ - /g, " ")
                  .split(" ")
                  .join("-")
                  .toLowerCase()}/prize-list`}
              >
                <a className={styles.purseLink}>
                  <div>{translationKeys["prize-list"]}</div>
                  <div>
                    <EastOutlinedIcon fontSize="large" />
                  </div>
                </a>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pursePageContainer}>
      <div className={styles.pursePageWrapper}>
        <ul className={styles.purseUlList}>
          <li>
            <span>{translationKeys.purse}</span>
            <span>
              $
              {getTotalPrize(
                playersCount,
                isElevated,
                isFinalSeason2
              ).toLocaleString("en-US")}
            </span>
          </li>
          <li>
            <span>{translationKeys.players}</span>
            <span>{isFinalSeason2 ? 100 : playersCount}</span>
          </li>
        </ul>
        <div className={styles.purseInfoBlocks}>
          <div className={styles.purseInfoBlock}>
            <span className={styles.purseCalculateTitle}>
              {translationKeys.calculate}
            </span>
            <span className={styles.purseCalculateText}>
              {translationKeys.rank}
            </span>
            <input
              className="text-black"
              type="number"
              defaultValue={inputVal}
              onChange={handleInputChange}
            />
            <div className={styles.purseCalculateTitle}>
              $
              {prizeList[parseInt(inputVal) - 1]?.toLocaleString("en-US") ||
                "0"}
            </div>
          </div>
          <div className={styles.purseInfoBlock}>
            <span>{translationKeys["stroke-play"]}</span>
            <ul className={styles.purseUlBlockList}>
              <li>
                <span>1st</span>
                <span>{prizeList[0]?.toLocaleString("en-US") || 0}</span>
              </li>
              <li>
                <span>2nd</span>
                <span>{prizeList[1]?.toLocaleString("en-US") || 0}</span>
              </li>
              <li>
                <span>3rd</span>
                <span>{prizeList[2]?.toLocaleString("en-US") || 0}</span>
              </li>
              <li>
                <span>4th</span>
                <span>{prizeList[3]?.toLocaleString("en-US") || 0}</span>
              </li>
              <li>
                <span>5th</span>
                <span>{prizeList[4]?.toLocaleString("en-US") || 0}</span>
              </li>
            </ul>
          </div>
          <div className={styles.purseInfoBlock}>
            <span>{translationKeys["aggregate-ctp"]}</span>
            <ul className={styles.purseUlBlockList}>
              <li>
                <span>1st</span>
                <span>{aggregateCtpPrizes()[0]}</span>
              </li>
              <li>
                <span>2nd</span>
                <span>{aggregateCtpPrizes()[1]}</span>
              </li>
              <li>
                <span>3rd</span>
                <span>{aggregateCtpPrizes()[2]}</span>
              </li>
            </ul>
          </div>
          {!isFinalSeason2 && (
            <div className={styles.purseInfoBlock}>
              <span>{translationKeys["birdie-streak"]}</span>
              <ul className={styles.purseUlBlockList}>
                <li>
                  <span>1st</span>
                  <span>{birdieStreakPrizes()[0]}</span>
                </li>
                <li>
                  <span>2nd</span>
                  <span>{birdieStreakPrizes()[1]}</span>
                </li>
                {!isRound456 && (
                  <li>
                    <span>3rd</span>
                    <span>{birdieStreakPrizes()[2]}</span>
                  </li>
                )}
              </ul>
            </div>
          )}
          {!isFinalSeason2 && (
            <div className={styles.purseInfoBlock}>
              <span>{translationKeys["longest-drive-men"]}</span>
              <ul className={styles.purseUlBlockList}>
                <li>
                  <span>1st</span>
                  <span>{longestDrivePrizes()[0]}</span>
                </li>
                <li>
                  <span>2nd</span>
                  <span>{longestDrivePrizes()[1]}</span>
                </li>
                <li>
                  <span>3rd</span>
                  <span>{longestDrivePrizes()[2]}</span>
                </li>
              </ul>
            </div>
          )}
          {!isFinalSeason2 && (
            <div className={styles.purseInfoBlock}>
              <span>{translationKeys["longest-drive-women"]}</span>
              <ul className={styles.purseUlBlockList}>
                <li>
                  <span>1st</span>
                  <span>{longestDrivePrizes()[0]}</span>
                </li>
                <li>
                  <span>2nd</span>
                  <span>{longestDrivePrizes()[1]}</span>
                </li>
                <li>
                  <span>3rd</span>
                  <span>{longestDrivePrizes()[2]}</span>
                </li>
              </ul>
            </div>
          )}
          {!isFinalSeason2 && !isRound456 && (
            <div className={styles.purseInfoBlock}>
              <span>{translationKeys["most-girs"]}</span>
              <ul className={styles.purseUlBlockList}>
                <li>
                  <span>1st</span>
                  <span>
                    {tournamentSeason === 1 ? MOST_GIRS_LAST[0] : MOST_GIRS[0]}
                  </span>
                </li>
                <li>
                  <span>2nd</span>
                  <span>
                    {tournamentSeason === 1 ? MOST_GIRS_LAST[1] : MOST_GIRS[1]}
                  </span>
                </li>
                <li>
                  <span>3rd</span>
                  <span>
                    {tournamentSeason === 1 ? MOST_GIRS_LAST[2] : MOST_GIRS[2]}
                  </span>
                </li>
              </ul>
            </div>
          )}
          {!isFinalSeason2 && (
            <div className={styles.purseInfoBlock}>
              <span>{translationKeys["social-media"]}</span>
              <ul className={styles.purseUlBlockList}>
                {tournamentSeason === 1 ? (
                  <>
                    <li>
                      <span>1st</span>
                      <span>{SOCIAL_MEDIA_LAST}</span>
                    </li>
                    <li>
                      <span>2nd</span>
                      <span>{SOCIAL_MEDIA_LAST}</span>
                    </li>
                    <li>
                      <span>3rd</span>
                      <span>{SOCIAL_MEDIA_LAST}</span>
                    </li>
                    <li>
                      <span>4th</span>
                      <span>{SOCIAL_MEDIA_LAST}</span>
                    </li>
                    <li>
                      <span>5th</span>
                      <span>{SOCIAL_MEDIA_LAST}</span>
                    </li>
                  </>
                ) : isFinalSeason2 ? (
                  <li>
                    <span>2x</span>
                    <span>{SOCIAL_MEDIA[1]}</span>
                  </li>
                ) : (
                  <>
                    {" "}
                    <li>
                      <span>2x</span>
                      <span>{SOCIAL_MEDIA[0]}</span>
                    </li>
                    <li>
                      <span>2x</span>
                      <span>{SOCIAL_MEDIA[1]}</span>
                    </li>
                  </>
                )}
              </ul>
            </div>
          )}
          <div
            className={`${styles.purseInfoBlock} ${styles.purseInfoBlockCentered}`}
          >
            <Link
              href={`/tournament/${tournamentName
                ?.replace(/:/g, "")
                .replace(/ - /g, " ")
                .split(" ")
                .join("-")
                .toLowerCase()}/prize-list`}
            >
              <a className={styles.purseLink}>
                <div>{translationKeys["prize-list"]}</div>
                <div>
                  <EastOutlinedIcon fontSize="large" />
                </div>
              </a>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Purse;
